import { ColorKeys, css, DownIcon, Inline, Notes, styled, UpIcon } from 'canoe';
import moment from 'moment';
import React, { useCallback, useMemo } from 'react';

import { AnalyticQuery } from '@candu-ai/analytics/types';
import { createArrayOfDate } from '@candu-ai/analytics/utils';

const StyledNumber = styled.span`
  font-size: 36px;
  line-height: 36px;
`;

const StyleNotes = styled(Notes)`
  margin: 0;
`;

export const DataSummary = styled.div<{ display: 'row' | 'column' }>`
  ${({ theme, display }) => css`
    padding: 0 ${theme.spaces.normal};

    ${display === 'column' &&
    css`
      margin-top: ${theme.spaces.normal};
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: row;

      & > div {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
    `}
  `}
`;

interface SummaryListProps<T = Record<string, number>> {
  entries: { label: string; value: string }[];
  rawData: AnalyticQuery<T>[];
  summaryType?: 'normal' | 'percentage';
  display?: 'row' | 'column';
}

export const SummaryList = <T extends Record<string, unknown>>({
  entries,
  rawData,
  summaryType,
  display = 'row',
}: SummaryListProps<T>) => {
  const colors = ['BrandPurple', 'AquaTeal', 'Warning', 'Danger', 'BerryBlue'] as ColorKeys[];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const axes = rawData[0]?.fields || [];
  const axis = axes[axes.length - 1];

  const reverseEntries = [...entries].reverse();
  const reversedColors = colors.slice(0, entries.length).reverse();
  const dateLabels: string[] = useMemo(() => {
    const dates = rawData.map((data) => createArrayOfDate(data?.metadata.time)).reverse();

    const dateString = dates.map(
      (dateRange) =>
        `${moment(dateRange?.[0]).utc().format('D MMM')} - ${
          moment(dateRange?.[dateRange?.length - 1])
            .utc()
            .startOf('day')
            .isSame(moment().utc().startOf('day'))
            ? 'Today'
            : moment(dateRange?.[dateRange?.length - 1])
                .utc()
                .format('D MMM')
        }`,
    );

    return dateString;
  }, [rawData]);

  const sumData: number[] = useMemo(() => {
    const reverseData = [...rawData].reverse();
    if (!reverseData[0]?.metadata) {
      return [];
    }

    return reverseData.map((res) => {
      if (summaryType === 'percentage') {
        const summaryInputs = res?.rows.reduce(
          ([sum, coef], row) => [
            sum + (row[axis.name] as number) * (row.coefficient as number),
            coef + (row.coefficient as number),
          ],
          [0, 0],
        );
        return summaryInputs[1] !== 0 ? summaryInputs[0] / summaryInputs[1] : 0;
      }

      return res?.rows.reduce((acc, row) => acc + (row[axis.name] as number) ?? 0, 0);
    });
  }, [rawData, summaryType, axis]);

  const sumDataLength = sumData.length;
  const dataToCompareWith = sumData[0];

  const getIcon = useCallback(
    (idx) => {
      if (sumData[idx] > dataToCompareWith) {
        return <UpIcon size="l" color="Success" />;
      }
      if (sumData[idx] < dataToCompareWith) {
        return <DownIcon size="l" color="Danger" />;
      }
      return null;
    },
    [sumData, dataToCompareWith],
  );

  return (
    <DataSummary display={display}>
      {reverseEntries.map(({ value, label }, idx) =>
        display === 'row' ? (
          <Inline ml="s" key={value} align="center" justify="space-between" space="none">
            <span>{label}</span>
            <StyledNumber color={reversedColors[idx]}>
              {summaryType === 'percentage'
                ? `${(sumData[idx] ?? 0).toFixed(1)}%`
                : sumData[idx]?.toLocaleString('en-GB')}
            </StyledNumber>
          </Inline>
        ) : (
          <div key={value}>
            <Inline align="center" justify="space-between" space="none">
              <StyleNotes color={reversedColors[idx]}>{dateLabels[idx]}</StyleNotes>
            </Inline>
            <Inline align="center" justify="space-between" space="none">
              {idx === sumDataLength - 1 ? getIcon(idx) : null}
              <StyledNumber color={reversedColors[idx]}>
                {summaryType === 'percentage'
                  ? `${(sumData[idx] ?? 0).toFixed(1)}%`
                  : sumData[idx]?.toLocaleString('en-GB')}
              </StyledNumber>
            </Inline>
          </div>
        ),
      )}
    </DataSummary>
  );
};
